import {
  addDays,
  differenceInCalendarDays,
  max,
  min,
  parseISO,
  startOfDay,
} from 'date-fns';

export interface HiresToDateTimeline {
  start: Date;
  end: Date;
  /** The example applicant's entry day, inside the range. */
  sourced: Date;
  /** The example applicant's hire day, after the range. */
  hired: Date;
  /** Positions along the bar from the range start to today, 0–100. */
  endPercent: number;
  sourcedPercent: number;
  hiredPercent: number;
}

/**
 * The explainer's timeline for a past range: the range, today, and an example
 * applicant sourced 2 days before the end and hired 4 days after it, so the
 * example always lands on either side of this range's own end date.
 * @param startDate - The range's first day. eg '2026-08-01'
 * @param endDate - The range's last day, before today. eg '2026-08-31'
 * @param today - The day hires to date count to.
 * @returns The dates and their positions on the bar.
 */
export function hiresToDateTimeline(
  startDate: string,
  endDate: string,
  today: Date,
): HiresToDateTimeline {
  const start = startOfDay(parseISO(startDate));
  const end = startOfDay(parseISO(endDate));
  const now = startOfDay(today);
  const sourced = max([start, addDays(end, -2)]);
  const hired = min([now, addDays(end, 4)]);
  const span = Math.max(1, differenceInCalendarDays(now, start));
  const percent = (day: Date) =>
    Math.min(
      100,
      Math.max(0, (differenceInCalendarDays(day, start) / span) * 100),
    );

  return {
    start,
    end,
    sourced,
    hired,
    endPercent: percent(end),
    sourcedPercent: percent(sourced),
    hiredPercent: percent(hired),
  };
}
